/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Search, Plus } from 'lucide-react';
import { UserContext } from '../../../contexts/User';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { formatDate } from '../../../utils/time';
import '../settings.css';
import DataTable from '../../../components/DataTable';
import RowActionMenu from '../../../components/RowActionMenu';
import useDebounce from '../../../hooks/useDebounce';
import EditDesignationModal from './EditModal';
import DesignationDeleteModal from './DeleteModal';

function DesignationsTab() {
  const { activeWorkspace } = useContext(UserContext);
  const [designations, setDesignations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState({});
  const { debounce } = useDebounce();

  const getDesignations = useCallback(async () => {
    if (!activeWorkspace) return;
    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.designations(activeWorkspace.id),
      params: { name: searchQuery, ...(sortKey ? { sortKey, sortOrder: sortDir } : {}) },
    });
    if (response.success) setDesignations(response.data?.designations ?? []);
    setIsLoading(false);
  }, [activeWorkspace, searchQuery, sortKey, sortDir]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(debounce((q) => setSearchQuery(q), 500), []);
  const handleSearchChange = (e) => { setSearchInput(e.target.value); handleSearch(e.target.value); };

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  useEffect(() => { getDesignations(); }, [getDesignations]);

  const openEdit = (d) => { setSelectedDesignation(d); setIsEditing(true); setShowEditModal(true); };
  const openDelete = (d) => { setSelectedDesignation(d); setShowDeleteConfirmModal(true); };

  const columns = [
    { key: 'name', label: 'Designation Name', sortable: true, width: 320, flex: true, render: (d) => <span className="font-medium text-ink-900">{d.name}</span> },
    { key: 'createdAt', label: 'Date Created', sortable: true, width: 200, render: (d) => <span className="text-ink-700 whitespace-nowrap">{formatDate(d.createdAt)}</span> },
    {
      key: 'actions',
      label: '',
      width: 72,
      align: 'center',
      render: (d) => (
        <RowActionMenu
          ariaLabel={`Actions for ${d.name}`}
          items={[
            { label: 'Edit Designation', onClick: () => openEdit(d) },
            { label: 'Delete Designation', destructive: true, onClick: () => openDelete(d) },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      {showEditModal && (
        <EditDesignationModal
          designation={selectedDesignation}
          isEditing={isEditing}
          closeModal={() => setShowEditModal(false)}
          onDesignationSaved={getDesignations}
          workspaceId={activeWorkspace.id}
        />
      )}
      {showDeleteConfirmModal && (
        <DesignationDeleteModal
          designation={selectedDesignation}
          closeModal={() => setShowDeleteConfirmModal(false)}
          getData={getDesignations}
        />
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-ink-900 mb-0.5">Designations</h4>
          <p className="text-sm text-ink-500">A designation is the job title or role assigned to a member</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <div className="relative" style={{ height: 40, width: 240 }}>
            <Search size={14} className="text-ink-500 pointer-events-none" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search designations"
              className="w-full rounded-[8px] bg-white text-ink-900 placeholder:text-ink-500 border border-rule focus:outline-none focus:border-brand-500"
              style={{ height: 40, paddingLeft: 36, paddingRight: 12, fontSize: 14, lineHeight: '20px', borderStyle: 'solid', boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="button"
            onClick={() => { setIsEditing(false); setSelectedDesignation({}); setShowEditModal(true); }}
            data-testid="new-designation"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-[#1a3156] transition flex-shrink-0"
          >
            <Plus size={16} />
            Add Designation
          </button>
        </div>
      </div>

      <DataTable
        bare
        columns={columns}
        rows={designations}
        getRowKey={(d) => d.id}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={isLoading}
        emptyText="No designations found."
        minWidth={640}
      />
    </>
  );
}

export default DesignationsTab;
